import { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Toolbar,
  Typography,
  Button,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Container,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Storefront as StorefrontIcon,
  LocalShipping as ShippingIcon,
  AddCircleOutline as BookIcon,
  Code as CodeIcon,
  Folder as FolderIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import { logout, getToken } from '../services/auth';
import { getAddressSuggestions, AddressSuggestion } from '../services/gps';
import { User, DealerInfo } from '../types/user';
import { DealerLogo } from './DealerLogo';

// Context for sharing dealer info across dealer pages
interface DealerContextType {
  dealerInfo: DealerInfo | null;
  user: User | null;
  refreshDealerInfo: () => Promise<void>;
}

export const DealerContext = createContext<DealerContextType>({
  dealerInfo: null,
  user: null,
  refreshDealerInfo: async () => {},
});

// Custom hook to use dealer context
export const useDealerContext = () => useContext(DealerContext);

const DealerAddressAutocomplete: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const timeout = useRef<NodeJS.Timeout>();
  const fetch = useCallback((q: string) => {
    if (timeout.current) clearTimeout(timeout.current);
    if (q.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    timeout.current = setTimeout(async () => {
      try { setSuggestions(await getAddressSuggestions(q, 6)); } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 300);
  }, []);
  return (
    <Autocomplete
      freeSolo
      options={suggestions}
      getOptionLabel={(o) => typeof o === 'string' ? o : o.address}
      inputValue={value}
      onInputChange={(_, v) => { onChange(v); fetch(v); }}
      onChange={(_, v) => { if (v && typeof v !== 'string') onChange(v.address); }}
      loading={loading}
      renderOption={(props, option) => (
        <li {...props}><Box><Typography variant="body2">{option.address}</Typography><Typography variant="caption" color="text.secondary">{option.region}</Typography></Box></li>
      )}
      renderInput={(params) => (
        <TextField {...params} fullWidth label="Dealership Address" placeholder="123 Main St, City, State, ZIP"
          helperText="Used as default pickup location for quotes" />
      )}
    />
  );
};

const DealerLayout = () => {
  const [user, setUser] = useState<User | null>(null);
  const [dealerInfo, setDealerInfo] = useState<DealerInfo | null>(null);
  const [logoPresignedUrl, setLogoPresignedUrl] = useState<string | null>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    dealershipName: '',
    address: '',
    website: '',
    notificationEmail: '',
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Map routes to tab indices
  const getTabIndex = (pathname: string): number => {
    if (pathname === '/dealerold' || pathname === '/dealerold/') return 0;
    if (pathname.includes('/dealerold/track')) return 1;
    if (pathname.includes('/dealerold/book')) return 2;
    if (pathname.includes('/dealerold/calculator')) return 3;
    if (pathname.includes('/dealerold/iframe-builder')) return 4;
    if (pathname.includes('/dealerold/resources')) return 5;
    return 0;
  };

  const [tabValue, setTabValue] = useState(getTabIndex(location.pathname));

  const loadUserData = async () => {
    // Get user from localStorage first for immediate display
    const userJson = localStorage.getItem('lima_user');
    if (userJson) {
      const userData = JSON.parse(userJson);
      setUser(userData);
      setDealerInfo(userData.dealerInfo || null);
      
      // Initialize profile form from localStorage
      if (userData.dealerInfo) {
        setProfileForm({
          dealershipName: userData.dealerInfo.dealershipName || '',
          address: userData.dealerInfo.address || '',
          website: userData.dealerInfo.website || '',
          notificationEmail: userData.dealerInfo.notificationEmail || userData.uname || '',
        });
      }
    }
    
    // Then fetch fresh data from database
    try {
      const token = getToken();
      if (!token) return;
      
      const response = await fetch('/.netlify/functions/dealer-profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded dealer profile from DB on mount:', data);
        
        // Update state with database values
        setDealerInfo(data.dealerInfo);
        if (data.logoPresignedUrl) setLogoPresignedUrl(data.logoPresignedUrl);
        
        // Update form
        if (data.dealerInfo) {
          setProfileForm({
            dealershipName: data.dealerInfo.dealershipName || '',
            address: data.dealerInfo.address || '',
            website: data.dealerInfo.website || '',
            notificationEmail: data.dealerInfo.notificationEmail || '',
          });
        }
        
        // Update localStorage with fresh data
        if (userJson) {
          const userData = JSON.parse(userJson);
          userData.dealerInfo = data.dealerInfo;
          localStorage.setItem('lima_user', JSON.stringify(userData));
          setUser(userData);
        }
      }
    } catch (err) {
      console.error('Error loading dealer profile on mount:', err);
    }
  };

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    setTabValue(getTabIndex(location.pathname));
  }, [location.pathname]);

  const refreshDealerInfo = async () => {
    try {
      const token = getToken();
      if (!token) return;
      
      const response = await fetch('/.netlify/functions/dealer-profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Update local state
        setDealerInfo(data.dealerInfo);
        if (data.logoPresignedUrl) setLogoPresignedUrl(data.logoPresignedUrl);
        
        // Update localStorage
        const userJson = localStorage.getItem('lima_user');
        if (userJson) {
          const userData = JSON.parse(userJson);
          userData.dealerInfo = data.dealerInfo;
          userData.clientName = data.clientName;
          localStorage.setItem('lima_user', JSON.stringify(userData));
          setUser(userData);
        }
        
        // Update form
        if (data.dealerInfo) {
          setProfileForm({
            dealershipName: data.dealerInfo.dealershipName || '',
            address: data.dealerInfo.address || '',
            website: data.dealerInfo.website || '',
            notificationEmail: data.dealerInfo.notificationEmail || '',
          });
        }
      }
    } catch (err) {
      console.error('Error refreshing dealer info:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      const loginUrl = dealerInfo?.dealershipId ? `/login?dealershipId=${encodeURIComponent(dealerInfo.dealershipId)}` : '/login';
      navigate(loginUrl);
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      const loginUrl = dealerInfo?.dealershipId ? `/login?dealershipId=${encodeURIComponent(dealerInfo.dealershipId)}` : '/login';
      navigate(loginUrl);
      window.location.reload();
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    switch (newValue) {
      case 0:
        navigate('/dealerold');
        break;
      case 1:
        navigate('/dealerold/track');
        break;
      case 2:
        navigate('/dealerold/book');
        break;
      case 3:
        navigate('/dealerold/calculator');
        break;
      case 4:
        navigate('/dealerold/iframe-builder');
        break;
      case 5:
        navigate('/dealerold/resources');
        break;
    }
  };

  const handleOpenProfileDialog = async () => {
    setError(null);
    setSuccess(null);
    setLoadingProfile(true);
    setProfileDialogOpen(true);
    
    // Always fetch fresh data from database when opening dialog
    try {
      const token = getToken();
      if (!token) {
        setError('No authentication token found');
        setLoadingProfile(false);
        return;
      }
      
      const response = await fetch('/.netlify/functions/dealer-profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched dealer profile from DB:', data);
        
        // Update state with fresh data
        setDealerInfo(data.dealerInfo);
        if (data.logoPresignedUrl) setLogoPresignedUrl(data.logoPresignedUrl);
        
        // Update form with fresh data
        if (data.dealerInfo) {
          setProfileForm({
            dealershipName: data.dealerInfo.dealershipName || '',
            address: data.dealerInfo.address || '',
            website: data.dealerInfo.website || '',
            notificationEmail: data.dealerInfo.notificationEmail || user?.uname || '',
          });
        }
        
        // Update localStorage
        const userJson = localStorage.getItem('lima_user');
        if (userJson) {
          const userData = JSON.parse(userJson);
          userData.dealerInfo = data.dealerInfo;
          localStorage.setItem('lima_user', JSON.stringify(userData));
        }
      } else {
        console.error('Failed to fetch dealer profile:', response.status);
        // Fallback to current values if fetch fails
        if (dealerInfo) {
          setProfileForm({
            dealershipName: dealerInfo.dealershipName || '',
            address: dealerInfo.address || '',
            website: dealerInfo.website || '',
            notificationEmail: dealerInfo.notificationEmail || user?.uname || '',
          });
        }
      }
    } catch (err) {
      console.error('Error fetching dealer profile:', err);
      // Fallback to current values if fetch fails
      if (dealerInfo) {
        setProfileForm({
          dealershipName: dealerInfo.dealershipName || '',
          address: dealerInfo.address || '',
          website: dealerInfo.website || '',
          notificationEmail: dealerInfo.notificationEmail || user?.uname || '',
        });
      }
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload an image file (JPG, PNG or WEBP)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file size must be less than 5MB');
        return;
      }
      
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile || !dealerInfo?.dealershipId) return;
    
    try {
      setUploadingLogo(true);
      setError(null);
      
      const token = getToken();
      if (!token) {
        setError('No authentication token found');
        return;
      }

      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = (reader.result as string).split(',')[1];
        
        const response = await fetch('/.netlify/functions/dealer-logo', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            dealershipId: dealerInfo.dealershipId,
            fileName: logoFile.name,
            fileData: base64Data,
            contentType: logoFile.type
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to upload logo');
        }

        // Update dealer info with new logo URL
        await refreshDealerInfo();
        
        setSuccess('Logo uploaded successfully!');
        setLogoFile(null);
        setLogoPreview(null);
      };
      
      reader.readAsDataURL(logoFile);
      
    } catch (err: any) {
      console.error('Error uploading logo:', err);
      setError(err.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const token = getToken();
      if (!token) {
        setError('No authentication token found');
        return;
      }

      // Upload logo first if there's a new one
      if (logoFile && dealerInfo?.dealershipId) {
        await handleUploadLogo();
      }

      const response = await fetch('/.netlify/functions/dealer-profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          dealerInfo: profileForm
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update profile');
      }

      // Update local state and localStorage
      const updatedDealerInfo = data.dealerInfo;
      setDealerInfo(updatedDealerInfo);
      if (data.logoPresignedUrl) setLogoPresignedUrl(data.logoPresignedUrl);
      
      const userJson = localStorage.getItem('lima_user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        userData.dealerInfo = updatedDealerInfo;
        localStorage.setItem('lima_user', JSON.stringify(userData));
        setUser(userData);
      }

      setSuccess('Profile updated successfully!');
      setTimeout(() => {
        setProfileDialogOpen(false);
        setSuccess(null);
      }, 1500);

    } catch (err: any) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      
      {/* Main Header */}
      <AppBar 
        position="static" 
        sx={{ 
          background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
        }}
      >
        <Toolbar>
          {dealerInfo?.dealershipId ? (
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.95)',
                borderRadius: 1,
                p: '3px',
                mr: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 0,
              }}
            >
              <DealerLogo
                dealershipId={dealerInfo.dealershipId}
                dealershipName={dealerInfo.dealershipName}
                variant="header"
                showFallback={true}
                preloadedUrl={logoPresignedUrl}
                refreshKey={dealerInfo?.logoUrl}
                sx={{
                  height: { xs: 28, sm: 34 },
                  maxWidth: { xs: 90, sm: 110 },
                  mr: 0,
                  display: 'block',
                }}
              />
            </Box>
          ) : (
            <StorefrontIcon sx={{ mr: 1.5, fontSize: 28 }} />
          )}
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 600,
              letterSpacing: '0.5px'
            }}
          >
            {dealerInfo?.dealershipName || 'Dealer Portal'}
          </Typography>
          
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 2 }}>
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: '#fff',
                  border: '2px solid rgba(255,255,255,0.4)'
                }}
              >
                {dealerInfo?.dealershipId ? (
                  <DealerLogo
                    dealershipId={dealerInfo.dealershipId}
                    dealershipName={dealerInfo.dealershipName}
                    variant="avatar"
                    showFallback={true}
                    preloadedUrl={logoPresignedUrl}
                    refreshKey={dealerInfo?.logoUrl}
                  />
                ) : (
                  <StorefrontIcon sx={{ fontSize: 18 }} />
                )}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                  {dealerInfo?.dealershipName || user.clientName || user.uname}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {dealerInfo?.dealershipId || 'Dealer Account'}
                </Typography>
              </Box>
              <Chip
                label="DEALER"
                size="small"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 22
                }}
              />
            </Box>
          )}

          <Tooltip title="Edit Profile">
            <IconButton
              color="inherit"
              onClick={handleOpenProfileDialog}
              sx={{ 
                mr: 1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          
          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{ 
              borderRadius: 2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            {!isMobile && 'Logout'}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Navigation Tabs */}
      <Box 
        sx={{ 
          bgcolor: 'white', 
          borderBottom: 1, 
          borderColor: 'divider',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}
      >
        <Container maxWidth="xl">
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
                minHeight: 56,
                px: 3,
              },
              '& .Mui-selected': {
                color: '#1a237e',
                fontWeight: 600,
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                bgcolor: '#1a237e',
              },
            }}
          >
            <Tab 
              icon={<DashboardIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start" 
              label="Dashboard" 
            />
            <Tab 
              icon={<ShippingIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start" 
              label="Track Shipments" 
            />
            <Tab 
              icon={<BookIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start" 
              label="Book a Shipment" 
            />
            <Tab 
              icon={<CalculateIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start" 
              label="Calculator" 
            />
            <Tab 
              icon={<CodeIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start" 
              label="Website Widget" 
            />
            <Tab 
              icon={<FolderIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start" 
              label="Resources" 
            />
          </Tabs>
        </Container>
      </Box>
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: '#f5f7fa',
          p: { xs: 2, sm: 3 },
        }}
      >
        <Container maxWidth="xl">
          <DealerContext.Provider value={{ dealerInfo, user, refreshDealerInfo }}>
            <Outlet />
          </DealerContext.Provider>
        </Container>
      </Box>

      {/* Footer */}
      <Box 
        component="footer" 
        sx={{ 
          py: 2, 
          px: 3, 
          bgcolor: 'white',
          borderTop: '1px solid',
          borderColor: 'divider',
          textAlign: 'center'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} LIMA Automotive Industries. All rights reserved.
        </Typography>
      </Box>

      {/* Profile Edit Dialog */}
      <Dialog 
        open={profileDialogOpen} 
        onClose={() => !saving && setProfileDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          Edit Dealership Profile
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          
          {loadingProfile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading profile data from database...
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Update your dealership information. This will be used to auto-fill forms across the portal.
              </Typography>

              {/* Logo Upload Section */}
              <Box sx={{ mb: 3, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Dealership Logo</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                  {(logoPreview || dealerInfo?.dealershipId) && (
                    logoPreview ? (
                      <Box
                        component="img"
                        src={logoPreview}
                        alt="Logo preview"
                        sx={{
                          width: 80,
                          height: 80,
                          minWidth: 80,
                          objectFit: 'contain',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                          bgcolor: 'white'
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          minWidth: 80,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                          bgcolor: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <DealerLogo
                          dealershipId={dealerInfo!.dealershipId}
                          dealershipName={dealerInfo!.dealershipName}
                          variant="profile"
                          showFallback={true}
                          preloadedUrl={logoPresignedUrl}
                          sx={{ width: '100%', height: '100%' }}
                        />
                      </Box>
                    )
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      disabled={uploadingLogo}
                      sx={{ mb: 0.5 }}
                    >
                      {logoFile ? 'Change Logo' : (dealerInfo?.logoUrl ? 'Replace Logo' : 'Upload Logo')}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleLogoFileChange}
                      />
                    </Button>
                    {logoFile && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleUploadLogo}
                        disabled={uploadingLogo}
                        sx={{ ml: 1 }}
                      >
                        {uploadingLogo ? 'Uploading...' : 'Upload Now'}
                      </Button>
                    )}
                    <Typography variant="caption" display="block" color="text.secondary">
                      JPG, PNG, WEBP, or SVG (max 5MB)
                    </Typography>
                    {logoFile && (
                      <Typography variant="caption" display="block" color="success.main">
                        Selected: {logoFile.name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Dealership Name"
                    value={profileForm.dealershipName}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, dealershipName: e.target.value }))}
                    placeholder="Premium Auto Sales"
                    helperText="Your dealership's display name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <DealerAddressAutocomplete
                    value={profileForm.address}
                    onChange={(addr) => setProfileForm(prev => ({ ...prev, address: addr }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={profileForm.website}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://www.yourdealership.com"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Notification Email"
                    type="email"
                    value={profileForm.notificationEmail}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, notificationEmail: e.target.value }))}
                    placeholder="sales@yourdealership.com"
                    helperText="Receive quote notifications"
                  />
                </Grid>
              </Grid>

              {dealerInfo?.dealershipId && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f7fa', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Dealer ID: <strong>{dealerInfo.dealershipId}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    (Contact support to change your Dealer ID)
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setProfileDialogOpen(false)} 
            disabled={saving || loadingProfile}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveProfile}
            disabled={saving || loadingProfile}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DealerLayout;

